import React, { useContext, useState, useRef } from "react"; // 1. useRef add kiya
import { useNavigate, useLocation } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Col, Row, Spinner } from "react-bootstrap";
import Header from "../Header/Header";
import { toastError, toastSuccess, toastWarning } from "../Common/ToastAlert";
import { Trash2, UploadCloud, RefreshCw } from "lucide-react"; // 2. Icons add kiye

// APIs import
import {
  createNewsFeed,
  updateNewsFeed,
  uploadImageToS3,
} from "../../api/network";
import { NetworkContext } from "../../context/NetworkContext";
import TextField from "../Common/TextField";
import BlogTextEditor from "./RichTextEditor";

// --- HELPER FUNCTION: Base64 to File ---
const dataURLtoFile = (dataurl, filename) => {
  try {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  } catch (e) {
    return null;
  }
};

// Compress the Images

const compressImage = (file, maxWidth, maxHeight, quality) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(dataUrl);
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

const CreateNewFeed = ({ refetchData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const networkId = localStorage.getItem("networkId");
  const { setAddFeed } = useContext(NetworkContext);

  // Data Extraction
  const existingData = location.state?.data;
  const isEditMode = !!existingData;

  // States
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(
    isEditMode && existingData?.image ? existingData.image : null
  );
  const [thumbnailChanged, setThumbnailChanged] = useState(false);

  const [error, setError] = useState({});

  // 3. REF banaya file input control karne ke liye
  const fileInputRef = useRef(null);

  const getInitialContent = (content) => {
    if (!content) return "";
    try {
      if (
        typeof content === "string" &&
        (content.startsWith("{") || content.startsWith("["))
      ) {
        return JSON.parse(content);
      }
      return content;
    } catch (e) {
      return content;
    }
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      title: existingData?.title || "",
      subtitle: existingData?.subtitle || "",
      hashtag: existingData?.hashtag || "",
      content: getInitialContent(existingData?.content),
    },
  });

  // --- LOGIC: Content ke andar ki images ko S3 pe bhejna ---
  const processContentImages = async (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== "string") return htmlContent;

    const div = document.createElement("div");
    div.innerHTML = htmlContent;
    const images = div.getElementsByTagName("img");
    const uploadPromises = [];

    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      if (img.src.startsWith("data:image")) {
        const file = dataURLtoFile(img.src, `content-${Date.now()}-${i}.png`);

        if (file) {
          const uploadPromise = async () => {
            const formData = new FormData();
            formData.append("fileName", file);

            try {
              const uploadRes = await uploadImageToS3(formData);
              if (uploadRes.status === 200 && uploadRes.data?.publicUrl) {
                img.src = uploadRes.data.publicUrl;
              }
            } catch (err) {
              console.error("Content Image Upload Error:", err);
            }
          };
          uploadPromises.push(uploadPromise());
        }
      }
    }

    if (uploadPromises.length > 0) {
      await Promise.all(uploadPromises);
    }
    return div.innerHTML;
  };

  const handleFormSubmit = async (data) => {
    setLoading(true);

    try {
      const finalThumbnail = selectedImage || existingData?.image || "";
      let contentToSend = data.content;

      if (typeof data.content === "string") {
        contentToSend = await processContentImages(data.content);
      } else if (typeof data.content === "object") {
        contentToSend = JSON.stringify(data.content);
      }

      const payload = {
        title: data.title,
        subtitle: data.subtitle,
        hashtag: data.hashtag,
        content: contentToSend,
        image: finalThumbnail,
      };

      let response;
      if (isEditMode) {
        response = await updateNewsFeed({
          ...payload,
          id: existingData.id,
          status: existingData.status,
        });
      } else {
        response = await createNewsFeed({ ...payload, networkId });
      }

      if (response.status === 200 || response.status === 201) {
        toast.success(
          isEditMode
            ? "Feed updated successfully"
            : "Feed created successfully",
          toastSuccess()
        );

        setLoading(false);
        navigate("/news-feed");

        if (!isEditMode) {
          setAddFeed({
            networkId,
            title: "",
            subtitle: "",
            hashtag: "",
            content: "",
            image: "",
          });
          setSelectedImage(null);
        } else {
          if (refetchData) refetchData();
        }
      } else {
        setLoading(false);
        toast.error("Failed to save feed", toastError());
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
      toast.warning("Something went wrong", toastWarning());
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileType = file.type.toLowerCase();
      const validTypes = ["image/jpeg", "image/jpg", "image/png"];

      // Basic Type Validation
      if (!validTypes.includes(fileType)) {
        if (fileInputRef.current) fileInputRef.current.value = "";
        setSelectedImage(null);
        setError((prev) => ({
          ...prev,
          selectedImage: "Only JPG, JPEG, and PNG files are allowed.",
        }));
        return;
      }

      try {
        setLoading(true); // Optional: Agar aap compression ke waqt spinner dikhana chahen

        // Compression call: Max width 1200px, quality 0.7
        const compressedBase64 = await compressImage(file, 1200, 1200, 0.7);

        setSelectedImage(compressedBase64);
        setThumbnailChanged(true);
        setError((prev) => ({ ...prev, selectedImage: "" }));
      } catch (err) {
        console.error("Compression Error:", err);
        toast.error("Failed to process image. Please try another one.");
      } finally {
        setLoading(false);
      }
    }
  };

  // --- 5. NEW: Remove Image Function ---
  const handleRemoveImage = () => {
    setSelectedImage(null);
    setThumbnailChanged(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Input value reset ki taaki same file re-upload ho sake
    }
  };

  // --- 6. NEW: Trigger Upload Function ---
  const handleTriggerUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Hidden input click kiya
    }
  };

  const errorMessage = {
    title: { required: "Title is required", min: "Min 3 chars required" },
    subtitle: { required: "Subtitle is required", min: "Min 3 chars required" },
    hashtag: { required: "Tag is required", min: "Min 3 chars required" },
  };

  return (
    <div className="right-container">
      <div className="main-header">
        <Header title={isEditMode ? "Edit News Feed" : "Create News Feed"} />
      </div>
      <div className="inner-body-container">
        <div className="join-consortium-card">
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <Row>
              <Col lg={9} md={8} sm={12}>
                {/* Title */}
                <div className="mb-3">
                  <Controller
                    control={control}
                    name="title"
                    rules={{
                      required: errorMessage.title.required,
                      minLength: 3,
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Title"
                        placeholder="Enter Title"
                        error={errors?.title?.message}
                      />
                    )}
                  />
                </div>

                {/* Subtitle and Tags Row */}
                <Row>
                  <Col lg={6} md={12} sm={12}>
                    <div className="mb-3">
                      <Controller
                        control={control}
                        name="subtitle"
                        rules={{
                          required: errorMessage.subtitle.required,
                          minLength: 3,
                          maxLength: 30,
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Subtitle"
                            placeholder="Enter subtitle"
                            error={errors?.subtitle?.message}
                          />
                        )}
                      />
                    </div>
                  </Col>
                  <Col lg={6} md={12} sm={12}>
                    <div className="mb-3">
                      <Controller
                        control={control}
                        name="hashtag"
                        rules={{
                          required: errorMessage.hashtag.required,
                          minLength: 3,
                          maxLength: 30,
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Tags"
                            placeholder="Enter Tags"
                            error={errors?.hashtag?.message}
                          />
                        )}
                      />
                    </div>
                  </Col>
                </Row>

                {/* Content Editor */}
                <label className="inputbox-label mb-2">Content</label>
                <Controller
                  control={control}
                  name="content"
                  rules={{
                    required: "Content is required",
                    validate: (val) => {
                      if (!val) return "Content cannot be empty";
                      if (typeof val === "string" && val === "<p></p>")
                        return "Content cannot be empty";
                      return true;
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <BlogTextEditor
                        value={value}
                        onChange={onChange}
                        placeholder="Type your story here..."
                      />
                      {errors?.content && (
                        <span
                          style={{
                            color: "red",
                            fontSize: "12px",
                            marginTop: "5px",
                            display: "block",
                          }}
                        >
                          {errors.content.message}
                        </span>
                      )}
                    </>
                  )}
                />
              </Col>

              {/* --- 7. NEW RIGHT SIDE UI (Thumbnail Upload) --- */}
              <Col lg={3} md={4} sm={12}>
                <div
                  className="textfield-container sticky-top"
                  style={{ top: "20px" }}
                >
                  <label className="inputbox-label">
                    Thumbnail Image <br />
                    <small style={{ color: "#888", fontSize: 10 }}>
                      (max size 1 MB)
                    </small>
                  </label>

                  {/* Hidden Input File - Controlled via Ref */}
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />

                  {/* CUSTOM UPLOAD UI LOGIC */}
                  {!selectedImage ? (
                    // STATE 1: NO IMAGE (Upload Box)
                    <div
                      onClick={handleTriggerUpload}
                      className={`d-flex flex-column align-items-center justify-content-center ${
                        error.selectedImage ? "border-danger" : ""
                      }`}
                      style={{
                        border: "2px dashed #ccc",
                        borderRadius: "8px",
                        minHeight: "150px",
                        backgroundColor: "#f9f9f9",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <UploadCloud size={40} className="text-secondary mb-2" />
                      <span
                        className="text-secondary font-weight-bold"
                        style={{ fontSize: "14px" }}
                      >
                        Click to Upload
                      </span>
                    </div>
                  ) : (
                    <div
                      className="position-relative"
                      style={{
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        overflow: "hidden",
                        backgroundColor: "#fff",
                        height: "200px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Image Preview */}
                      <img
                        src={selectedImage}
                        alt="Thumbnail"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          objectPosition: "center",
                        }}
                      />
                      <div
                        className="d-flex justify-content-center gap-2 align-items-center"
                        style={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          width: "100%",
                          padding: "8px",
                          background: "rgba(255, 255, 255, 0.9)",
                          borderTop: "1px solid #ddd",
                        }}
                      >
                        <button
                          type="button"
                          onClick={handleTriggerUpload}
                          className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                          style={{ fontSize: "12px", padding: "4px 8px" }}
                        >
                          <RefreshCw size={14} /> Change
                        </button>
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                          style={{ fontSize: "12px", padding: "4px 8px" }}
                        >
                          <Trash2 size={14} /> Remove
                        </button>
                      </div>
                    </div>
                  )}

                  {error.selectedImage && (
                    <span className="inputbox-error-msg mt-2 d-block">
                      {error.selectedImage}
                    </span>
                  )}
                </div>
              </Col>
            </Row>

            {/* Buttons Row */}
            <Row>
              <Col lg={12} md={12} sm={12}>
                <div className="d-flex align-items-center gap-2 justify-content-end mt-5">
                  <button
                    type="button"
                    className="dsm-btn-secondary"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="dsm-btn-primary"
                    disabled={
                      loading || (isEditMode && !isDirty && !thumbnailChanged)
                    }
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" variant="light" />
                        {isEditMode ? " Updating..." : " Creating..."}
                      </>
                    ) : isEditMode ? (
                      "Update Feed"
                    ) : (
                      "Create Feed"
                    )}
                  </button>
                </div>
              </Col>
            </Row>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateNewFeed;
